import api from './client';
import type { User, UserTalent, DeviceStatus } from './types';

/**
 * Users API
 * Håndterer brukeradministrasjon og bruker-talent relasjoner
 */
export const usersAPI = {
  /** Alle brukere i innlogget (eller valgt kontekst-) organisasjon. */
  getAll: async (): Promise<{ users: User[] }> => {
    const response = await api.get('/users');
    return response.data;
  },

  getById: async (id: number): Promise<{ user: User }> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (userData: Partial<User>): Promise<{ user: User }> => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (id: number, userData: Partial<User>): Promise<{ user: User }> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

/**
 * User API (Extended)
 * Utvidet bruker-API med talent-relasjoner
 */
export const userAPI = {
  getAll: async (): Promise<{ users: User[] }> => {
    const response = await api.get('/users');
    return response.data;
  },

  getById: async (id: number): Promise<{ user: User }> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  /** Org styres av serveren (innlogget bruker eller superadmins org-kontekst). */
  getAllWithTalents: async (talentId?: number): Promise<{ users: any[] }> => {
    const path =
      talentId != null ? `/users/with-talents?talentId=${talentId}` : '/users/with-talents';
    const response = await api.get(path);
    return response.data;
  },

  create: async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string; // Påkrevd
    phoneCountryCode?: string; // Optional, default '+47' i backend
    roles?: string[];
    isActive?: boolean;
  }): Promise<{ user: User }> => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (id: number, userData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    currentPassword?: string;
    phoneNumber?: string;
    phoneCountryCode?: string;
    roles?: string[];
    isActive?: boolean;
  }): Promise<{ user: User }> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  bulkDelete: async (userIds: number[]): Promise<{ message: string; deletedIds: number[] }> => {
    const response = await api.post('/users/bulk-delete', { userIds });
    return response.data;
  },

  // Bruker-talent relasjoner
  getUserTalents: async (userId: number): Promise<{ talents: UserTalent[] }> => {
    const response = await api.get(`/users/${userId}/talents`);
    return response.data;
  },

  addUserTalent: async (userId: number, talentData: {
    talentId: number;
    erfaringsnivaa?: 'grunnleggende' | 'middels' | 'avansert' | 'ekspert';
    notater?: string;
  }): Promise<{ talent: UserTalent }> => {
    const response = await api.post(`/users/${userId}/talents`, talentData);
    return response.data;
  },

  updateUserTalent: async (userId: number, talentId: number, talentData: {
    erfaringsnivaa?: 'grunnleggende' | 'middels' | 'avansert' | 'ekspert';
    notater?: string;
  }): Promise<{ talent: UserTalent }> => {
    const response = await api.put(`/users/${userId}/talents/${talentId}`, talentData);
    return response.data;
  },

  removeUserTalent: async (userId: number, talentId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/users/${userId}/talents/${talentId}`);
    return response.data;
  },

  /** Oppdater egen profil (navn, telefon, e-post). Bruker alltid innlogget bruker. */
  updateMe: async (data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    phoneCountryCode?: string;
    currentPassword?: string;
  }): Promise<{ user: User }> => {
    const response = await api.put('/auth/me', data);
    return response.data;
  },

  // Device status for push notifications
  getDeviceStatus: async (userId: number): Promise<{ deviceStatus: DeviceStatus }> => {
    const response = await api.get(`/users/${userId}/device-status`);
    return response.data;
  },
};

